import { IErrorResponse } from 'types/service/errorCodes.interface';
import {
  IInfinitePageParams,
  IInfinitePageResponse,
  IInfinitePageStatisticsResponse,
} from 'services/types';
import { ICountry } from 'services/enums/types';

export interface ISettlementPeriodName {
  name: string;
  year: number;
}

export interface IStatementCurrency {
  name: string;
  id: number;
  currencyCode: string;
}

export interface IStatementExchangeRate {
  currencyId: number;
  currency: IStatementCurrency;
  rate: number;
  exchangeRateDate: string;
  tableCode: string;
}

export interface IStatementConsistency {
  name: string;
  id: number;
}

export interface IStatement {
  id: string;
  magellanAccountId?: string;
  salesChannelId: string;
  providerId: string;
  settlementPeriodId: string;
  statementStatus: STATEMENT_STATUS;
  defaultCurrency: number;
  defaultTotalValue: number;
  statementFileId: string;
  statementFilePath: string;
  settlementPeriodName: ISettlementPeriodName;
  salesChannelName: string;
  providerName: string;
  exchangeRates: IStatementExchangeRate[];
  errors: IErrorResponse[];
  consistency: IStatementConsistency | null;
  createdAt: string;
  editedAt: string | null;
  hasUnrelatedRows: boolean;
}

export interface IStatementStatistics {
  allAmount: number;
}

export type IStatementsResponse = IInfinitePageStatisticsResponse<
  IStatement,
  IStatementStatistics
>;

export interface IStatementsFilterParams extends IInfinitePageParams {
  magellanAccountId?: string;
  startSettlementPeriodId?: string;
  endSettlementPeriodId?: string;
  salesChannels?: string[];
  providers?: string[];
  hasUnrelatedRows?: boolean;
  showOtherStatuses?: boolean;
}

export interface IInconsistentStatementsFilterParams {
  magellanAccountId?: string;
  startSettlementPeriodId?: string;
  endSettlementPeriodId?: string;
  salesChannels?: string[];
}

export enum CURRENCY_ID {
  unknown = 0,
  PLN = 1,
  USD = 2,
  EUR = 3,
  CHF = 4,
  GBP = 5,
}

export enum STATEMENT_STATUS {
  unknown = -1,
  waiting = 1,
  done = 2,
  error = 3,
  canceled = 4,
  accepted = 5,
  inProgress = 6,
}

export enum STATEMENT_CONSISTANCY {
  consistent = 1,
  partiallyConsistent = 2,
  inconsistent = 3,
}

export enum PRODUCT_TYPE {
  unknown = 0,
  track = 1,
  album = 2,
  video = 3,
  merch = 4,
}

export interface IStatementChunkedUploadPayload {
  magellanAccountId: string;
  salesChannelId: string;
  providerId: string;
  settlementPeriodId: string;
  templateType: number;
  file: File;
  onProgress?: (progress: number) => void;
}

export interface IStatementChunkedUploadResponse {
  value: boolean;
  isSuccess: boolean;
  isFailure: boolean;
  errors: string[];
}

export interface IStatementRowProduct {
  productType: PRODUCT_TYPE;
  id: string;
  title: string;
  version: string;
  channelName: string;
}

export interface IStatementRow {
  id: string;
  statementId: string;
  ean: string | null;
  isrc: string | null;
  channelId: string | null;
  videoId: string | null;
  assetId: string | null;
  salesPeriodFrom: string;
  salesPeriodTo: string;
  quantity: number;
  unitPrice: number;
  totalValue: number;
  defaultUnitPrice: number;
  defaultTotalValue: number;
  ok: boolean;
  countryCode: string;
  currencyId: number;
  defaultCurrencyId: number;
  currency: IStatementCurrency;
  defaultCurrency: IStatementCurrency;
  country: ICountry;
  createdAt: string;
  editedAt: string | null;
}

export interface IStatementRowsFilterParams extends IInfinitePageParams {
  ean?: string;
  isrc?: string;
  channelId?: string;
  videoId?: string;
  assetId?: string;
}

export type IStatementRowsResponse = IInfinitePageResponse<IStatementRow>;

export interface IStatementUnrelatedRowsStatistics {
  allNetAmount: number;
}

export type IStatementUnrelatedRowsResponse = IInfinitePageStatisticsResponse<
  IStatementRow,
  IStatementUnrelatedRowsStatistics
>;

export interface IStatementPartiallyOrFullyUnrelatedRow extends IStatementRow {
  unrelatedIdentifiers: string[];
  isIsrcUnrelated: boolean;
  isEanUnrelated: boolean;
  isChannelIdUnrelated: boolean;
  isVideoIdUnrelated: boolean;
  isAssetIdUnrelated: boolean;
}

export type IStatementPartiallyOrFullyUnrelatedRowsResponse =
  IInfinitePageStatisticsResponse<
    IStatementPartiallyOrFullyUnrelatedRow,
    IStatementUnrelatedRowsStatistics
  >;
